import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { downloadPredictionDocx } from '../../utils/loadDocx.js';

const PredictionModal = ({ code, onClose, patientName, results }) => {
    const { t, i18n } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const [includePatientName, setIncludePatientName] = useState(true);
    
    if (!code) return null;
    
    // Получаем переводы заранее, чтобы использовать их в документе и на экране
    const title = t(`metrica:predictions.${code}.title`);
    const summary = t(`metrica:predictions.${code}.summary`);
    const details = t(`metrica:predictions.${code}.details`);
    
    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            await downloadPredictionDocx({
                code,
                title,
                summary,
                details,
                language: i18n.language,
                patientName: includePatientName ? patientName : null,
                results: results
            });
        } catch (err) {
            console.error('Download failed:', err);
            const lang = (i18n.language || 'ru').slice(0, 2);
            toast.error(
                lang === 'ru' ? 'Ошибка при скачивании файла' :
                lang === 'pt' ? 'Erro ao baixar o arquivo' :
                'Failed to download file'
            );
        } finally {
            setIsDownloading(false);
        }
    };
    
    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center p-5 border-b border-[#dee2e6] bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef]">
                    <h3 className="text-xl font-bold text-[#2c3e50]">{title}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-[#e9ecef] rounded-xl transition-colors"><X size={24} className="text-[#6c757d]" /></button>
                </div>
                
                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    
                    {/* --- ДОБАВЛЕННЫЙ БЛОК С ФИО --- */}
                    {patientName && (
                        <div className="mb-4 pb-4 border-b border-[#e9ecef]">
                            <p className="text-lg text-[#495057]">
                                <span className="font-bold text-[#2c3e50]">
                                    {{ ru: 'Пациент:', pt: 'Paciente:' }[(i18n.language || 'ru').slice(0, 2)] || 'Patient:'}
                                </span>{" "}
                                {patientName}
                            </p>
                        </div>
                    )}
                    {/* -------------------------------- */}

                    <p className="text-2xl text-[#000] whitespace-pre-line leading-relaxed mb-4 bg-[#e3f2fd]/50 p-4 rounded-xl border border-[#90caf9]">
                        {summary}
                    </p>
                    <div className="prose prose-sm max-w-none">
                        <p className="text-2xl text-[#000] whitespace-pre-line leading-relaxed">
                            {details}
                        </p>
                    </div>
                </div>
                
                {/* Footer with buttons */}
                <div className="p-5 border-t border-[#dee2e6] bg-[#f8f9fa] flex justify-between items-center flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="includePatientName"
                            checked={includePatientName}
                            onChange={(e) => setIncludePatientName(e.target.checked)}
                            className="w-5 h-5 accent-[#3498db] cursor-pointer"
                        />
                        <label htmlFor="includePatientName" className="text-lg font-medium text-[#2c3e50] cursor-pointer select-none">
                            {t('metrica:modal.includePatientName', 'Добавить ФИО пациента')}
                        </label>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={handleDownload}
                            disabled={isDownloading}
                            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-all shadow-md ${
                                isDownloading 
                                    ? 'bg-[#95a5a6] cursor-not-allowed text-white' 
                                    : 'bg-gradient-to-r from-[#27ae60] to-[#219653] hover:from-[#219653] hover:to-[#1e8449] text-white hover:shadow-lg'
                            }`}
                        >
                            {isDownloading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>{t('metrica:common.downloading', 'Скачивание...')}</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span>{t('metrica:common.downloadDocx', 'Скачать .docx')}</span>
                                </>
                            )}
                        </button>
                        
                        <button 
                            onClick={onClose} 
                            className="px-6 py-3 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-xl font-bold transition-colors shadow-md"
                        >
                            {t('metrica:common.close', 'Закрыть')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PredictionModal;
